import { useLocalSearchParams } from 'expo-router';
import React from 'react';
import { ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { RatingChip } from '@/components/match/PlayerRatings';
import { TeamCrest } from '@/components/matches/TeamCrest';
import { Scores365Profile } from '@/components/player/Scores365Profile';
import { AppText } from '@/components/ui/AppText';
import { BackHeader } from '@/components/ui/BackHeader';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { FollowButton } from '@/components/ui/FollowButton';
import { Screen } from '@/components/ui/Screen';
import { Skeleton } from '@/components/ui/Skeleton';
import { usePlayer, usePlayerSeasonStats } from '@/features/leagues/hooks';
import type { PlayerMatchRecent } from '@/lib/data/provider';
import { formatMatchDate, formatNumber, formatPrice, localizeDigits } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';

function StatTile({ label, value }: { label: string; value: string }) {
  return (
    <Card className="flex-1 items-center gap-1 px-2 py-3">
      <AppText variant="heading" weight="bold" tabular className="text-ink">
        {value}
      </AppText>
      <AppText variant="micro" className="text-center">
        {label}
      </AppText>
    </Card>
  );
}

/** One row of the player's per-match log: opponent · result · his line. */
function RecentMatchRow({ row }: { row: PlayerMatchRecent }) {
  const { numeralOptions, arabicNumerals } = useLocale();
  const fx = row.fixture;
  if (!fx) return null;
  const kickoff = fx.kickoff_at ? new Date(fx.kickoff_at) : null;
  const kickoffValid = kickoff && !Number.isNaN(kickoff.getTime());
  const playerIsHome = row.team_id != null && fx.home_team_id === row.team_id;
  const opponent = playerIsHome ? fx.away_team : fx.home_team;
  const score =
    fx.home_score != null && fx.away_score != null
      ? // Bidi-isolate so the home-away digits never swap in Arabic RTL.
        `⁦${localizeDigits(`${fx.home_score}-${fx.away_score}`, arabicNumerals)}⁩`
      : null;
  const markers: string[] = [];
  if (row.goals > 0) markers.push(`${row.goals}⚽`);
  if (row.assists > 0) markers.push(`${row.assists}🅰`);
  markers.push(`${row.minutes}′`);

  return (
    <View className="flex-row items-center gap-3 border-t border-line px-4 py-2.5">
      <TeamCrest uri={opponent?.logo_url} name={opponent?.name ?? '?'} size="sm" />
      <View className="flex-1">
        <AppText variant="caption" weight="semibold" className="text-ink" numberOfLines={1}>
          {opponent?.short_name ?? opponent?.name ?? '-'}
          {score ? `  ·  ${score}` : ''}
        </AppText>
        <AppText variant="micro" className="text-muted" numberOfLines={1}>
          {kickoffValid ? `${formatMatchDate(kickoff, numeralOptions)}  ·  ` : ''}
          {localizeDigits(markers.join(' '), arabicNumerals)}
        </AppText>
      </View>
      <RatingChip rating={row.rating} />
    </View>
  );
}

export default function PlayerScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const { t } = useTranslation();
  const { numeralOptions } = useLocale();
  const playerQuery = usePlayer(id);
  const statsQuery = usePlayerSeasonStats(id);

  if (playerQuery.isPending) {
    return (
      <Screen>
        <BackHeader />
        <View className="gap-4 px-4">
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-40 w-full" />
        </View>
      </Screen>
    );
  }

  if (playerQuery.isError || !playerQuery.data) {
    return (
      <Screen>
        <BackHeader />
        <ErrorState error={playerQuery.error} onRetry={() => playerQuery.refetch()} />
      </Screen>
    );
  }

  const player = playerQuery.data;
  const stats = statsQuery.data;
  const fmt = (value: number) => formatNumber(value, numeralOptions);

  // Clean sheets are a keeper/defender stat — show them only for those roles.
  // Position is normally a code (GK/DEF/MID/FWD) but tolerate full words and
  // single-letter variants from other data sources.
  const positionCode = (player.position ?? '').trim().toUpperCase();
  const isDefensivePlayer =
    positionCode.startsWith('G') || // GK, G, Goalkeeper, Gardien
    positionCode.startsWith('D'); // DEF, D, Defender, Défenseur

  return (
    <Screen>
      <BackHeader title={player.known_as ?? player.name} />
      <ScrollView contentContainerStyle={{ paddingBottom: 32 }}>
        <Card className="mx-4 mb-4 flex-row items-center gap-4 px-4 py-5">
          <TeamCrest uri={player.photo_url} name={player.known_as ?? player.name} size="xl" />
          <View className="flex-1 gap-1">
            <AppText variant="heading" numberOfLines={2}>
              {player.name}
            </AppText>
            <AppText variant="micro">
              {t(`player.positions.${player.position}`)}
              {player.nationality ? `  ·  ${player.nationality}` : ''}
            </AppText>
            {player.team ? (
              <View className="mt-1 flex-row items-center gap-1.5">
                <TeamCrest uri={player.team.logo_url} name={player.team.name} size="sm" />
                <AppText variant="micro" className="text-ink">
                  {player.team.name}
                </AppText>
              </View>
            ) : null}
          </View>
          <View className="items-center gap-2">
            <FollowButton entityType="player" entityId={id} />
            <View className="items-center rounded-2xl bg-volt/10 px-3 py-2">
              <AppText variant="heading" weight="bold" tabular className="text-volt">
                {formatPrice(player.fantasy_price, numeralOptions)}
              </AppText>
              <AppText variant="micro" className="text-volt-dim">
                {t('player.fantasyPrice')}
              </AppText>
            </View>
          </View>
        </Card>

        <View className="px-4 pb-2">
          <AppText variant="heading">{t('player.stats')}</AppText>
        </View>

        {statsQuery.isPending ? (
          <View className="gap-3 px-4">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </View>
        ) : statsQuery.isError ? (
          <ErrorState error={statsQuery.error} onRetry={() => statsQuery.refetch()} />
        ) : !stats || stats.appearances === 0 ? (
          <EmptyState
            icon="stats-chart-outline"
            title={t('player.noStatsTitle')}
            body={t('player.noStatsBody')}
          />
        ) : (
          <View className="gap-3 px-4">
            <View className="flex-row gap-3">
              <StatTile label={t('player.minutes')} value={fmt(stats.minutes)} />
              <StatTile label={t('player.goals')} value={fmt(stats.goals)} />
              <StatTile label={t('player.assists')} value={fmt(stats.assists)} />
            </View>
            <View className="flex-row gap-3">
              {/* Clean sheets are only meaningful for keepers and defenders; for
                  midfielders and forwards show appearances instead so the grid
                  stays full and relevant. */}
              {isDefensivePlayer ? (
                <StatTile
                  label={t('fantasy.myTeam.breakdown.clean_sheet')}
                  value={fmt(stats.cleanSheets)}
                />
              ) : (
                <StatTile
                  label={t('match.playerDetail.appearances')}
                  value={fmt(stats.appearances)}
                />
              )}
              <StatTile label={t('match.yellowCard')} value={fmt(stats.yellow)} />
              <StatTile
                label={t('player.matchRating')}
                value={stats.averageRating !== null ? String(stats.averageRating) : '-'}
              />
            </View>

            {stats.recent.some((row) => row.fixture) ? (
              <Card className="overflow-hidden">
                <View className="border-b border-line bg-surface-2/40 px-4 py-2.5">
                  <AppText variant="micro" weight="bold" className="uppercase tracking-widest text-muted">
                    {t('player.recentMatches')}
                  </AppText>
                </View>
                {stats.recent.map((row) => (
                  <RecentMatchRow key={row.id} row={row} />
                ))}
              </Card>
            ) : null}
          </View>
        )}

        {/* Rich 365scores profile (self-hides for unmapped players). */}
        <Scores365Profile playerName={player.known_as ?? player.name} />
      </ScrollView>
    </Screen>
  );
}
